import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  IonInput,
  ModalController,
  RadioGroupCustomEvent
} from '@ionic/angular';
import { catchError, of, Subscription } from 'rxjs';

import { VoidRefundReason } from 'src/app/models';
import { ReasonService } from 'src/app/services';

// Single-select callers (void/refund reasons today) get back one result.
// A future multiSelect: true caller gets back VoidRefundReason[] instead — cast accordingly.
// id is omitted when the user picked "Other" and typed a custom reason.
export interface ReasonSelectionModalResult {
  id?: number;
  name: string;
}

export interface ReasonSelectionModalProps {
  label: string;
  reasonType: 'void' | 'refund';
  /** Allow selecting more than one reason. Defaults to false (single select, radio UI). */
  multiSelect?: boolean;
}

// Sentinel radio value for the "Other" row — distinct from any real reason id.
const OTHER_REASON_ID = -1;
const OTHER_REASON_MAX_LENGTH = 25;

@Component({
  templateUrl: './reason-selection.modal.html',
  styleUrls: ['./reason-selection.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReasonSelectionModal implements OnInit, OnDestroy {
  @Input() label!: string;
  @Input() reasonType!: 'void' | 'refund';
  @Input() multiSelect = false;

  reasons: VoidRefundReason[] = [];
  selectedReasonIds = new Set<number>();
  loading = true;
  loadError = false;
  otherReasonText = '';

  readonly OTHER_REASON_ID = OTHER_REASON_ID;
  readonly OTHER_REASON_MAX_LENGTH = OTHER_REASON_MAX_LENGTH;

  @ViewChild('otherReasonInput') otherReasonInput?: IonInput;

  private fetchSubscription?: Subscription;

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly reasonService: ReasonService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.fetchReasons();
  }

  ngOnDestroy() {
    this.fetchSubscription?.unsubscribe();
  }

  fetchReasons() {
    this.loading = true;
    this.loadError = false;

    const reasons$ =
      this.reasonType === 'void'
        ? this.reasonService.getVoidReasons()
        : this.reasonService.getRefundReasons();

    this.fetchSubscription = reasons$
      .pipe(
        catchError(() => {
          this.loadError = true;
          return of<VoidRefundReason[]>([]);
        })
      )
      .subscribe((reasons) => {
        this.reasons = reasons;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  // Single select's current value, for the native ion-radio-group.
  get singleSelectedId(): number | null {
    const [first] = this.selectedReasonIds;
    return first ?? null;
  }

  isSelected(reasonId: number): boolean {
    return this.selectedReasonIds.has(reasonId);
  }

  get isOtherSelected(): boolean {
    return this.selectedReasonIds.has(OTHER_REASON_ID);
  }

  get isConfirmDisabled(): boolean {
    if (!this.selectedReasonIds.size) {
      return true;
    }
    return this.isOtherSelected && !this.otherReasonText.trim();
  }

  onRadioChange(event: Event) {
    const { value } = (event as RadioGroupCustomEvent<number>).detail;
    this.selectReason(value);
  }

  // The "Other" row can't be an ion-item (nesting the text input inside one
  // causes Ionic's item-click delegation to fight the input's own focus/click
  // handling and deselect the radio) — so it's a plain div, and this restores
  // the click-anywhere-in-the-row-to-select behaviour ion-item gave for free.
  onSelectOther() {
    this.selectReason(OTHER_REASON_ID);
  }

  private selectReason(value: number) {
    const otherJustSelected =
      value === OTHER_REASON_ID && !this.isOtherSelected;
    this.selectedReasonIds = new Set([value]);
    if (value !== OTHER_REASON_ID) {
      this.otherReasonText = '';
    }
    this.cdr.markForCheck();

    if (otherJustSelected) {
      // *ngIf renders the input this same tick — defer so it exists to focus.
      setTimeout(() => this.otherReasonInput?.setFocus());
    }
  }

  // Multi-select only: clicking the row toggles that reason's checkbox.
  onToggleReason(reasonId: number) {
    this.selectedReasonIds.has(reasonId)
      ? this.selectedReasonIds.delete(reasonId)
      : this.selectedReasonIds.add(reasonId);
    this.cdr.markForCheck();
  }

  onConfirm() {
    if (!this.selectedReasonIds.size) {
      return;
    }

    if (this.isOtherSelected) {
      const name = this.otherReasonText.trim();
      if (!name) {
        return;
      }
      this.modalCtrl.dismiss({ name });
      return;
    }

    const selected = this.reasons.filter((r) =>
      this.selectedReasonIds.has(r.id)
    );
    if (!selected.length) {
      return;
    }
    this.modalCtrl.dismiss(this.multiSelect ? selected : selected[0]);
  }

  onCloseModal() {
    this.modalCtrl.dismiss();
  }
}
